import React, { useRef, useEffect, useCallback } from "react";
import classes from "./DraggableAndResizableDiv.scss";
import ReactDOM from "react-dom";

const EDGE_DETECTION_THRESHOLD = 6;
const cursors = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
};
const DraggableAndResizableDiv = ({
  minWidth = 480,
  minHeight = 240,
  box,
  setBox,
  children,
}) => {
  const boxRef = useRef(null);
  const parentRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    if (parentRef?.current && !box?.isInitialized) {
      const button = document.getElementById("google-docs-playback-button");
      if (button) {
        const { x, y } = button.getBoundingClientRect();
        setBox(prev => ({
          ...prev,
          isInitialized: true,
          x: x - prev.width + 36,
          y: y - prev.height - 16,
        }));
      }
    }
  }, [box, setBox]);

  const getResizeDir = (e, rect) => {
    const { clientX: x, clientY: y } = e;
    const { left, right, top, bottom } = rect;

    if (Math.abs(x - left) < EDGE_DETECTION_THRESHOLD) return "w";
    if (Math.abs(x - right) < EDGE_DETECTION_THRESHOLD) return "e";
    if (Math.abs(y - top) < EDGE_DETECTION_THRESHOLD) return "n";
    if (Math.abs(y - bottom) < EDGE_DETECTION_THRESHOLD) return "s";

    return null;
  };

  const handleMouseDownForDragging = e => {
    if (!boxRef.current) return;
    e.preventDefault();
    setBox(prev => ({
      ...prev,
      isResizing: false,
      isDragging: true,
      offsetX: e.clientX - prev.x,
      offsetY: e.clientY - prev.y,
    }));
  };

  const handleMouseDownForResizing = e => {
    if (!boxRef.current) return;
    if (dragRef?.current?.contains(e.target)) return;
    const rect = boxRef.current.getBoundingClientRect();
    const dir = getResizeDir(e, rect);
    if (dir) {
      e.preventDefault();
      setBox(prev => ({
        ...prev,
        isDragging: false,
        isResizing: true,
        resizeDir: dir,
      }));
    }
  };

  const handleMouseMove = useCallback(
    e => {
      if (!boxRef.current || !parentRef.current) return;
      const rect = boxRef.current.getBoundingClientRect();
      const parentRect = parentRef.current.getBoundingClientRect();

      setBox(prev => {
        let { x, y, width, height } = prev;

        if (prev.isDragging) {
          x = e.clientX - prev.offsetX;
          y = e.clientY - prev.offsetY;

          // Clamp inside parent
          x = Math.max(0, Math.min(x, parentRect.width - width));
          y = Math.max(0, Math.min(y, parentRect.height - height));

          return { ...prev, x, y };
        }

        if (prev.isResizing) {
          switch (prev.resizeDir) {
            case "e": {
              const newWidth = e.clientX - parentRect.left - x;
              width = Math.max(
                minWidth,
                Math.min(newWidth, parentRect.width - x)
              );
              break;
            }
            case "s": {
              const newHeight = e.clientY - parentRect.top - y;
              height = Math.max(
                minHeight,
                Math.min(newHeight, parentRect.height - y)
              );
              break;
            }
            case "w": {
              const newX = e.clientX - parentRect.left;
              const newWidth = rect.right - e.clientX;
              if (newWidth >= minWidth && newX >= 0) {
                width = newWidth;
                x = newX;
              }
              break;
            }
            case "n": {
              const newY = e.clientY - parentRect.top;
              const newHeight = rect.bottom - e.clientY;
              if (newHeight >= minHeight && newY >= 0) {
                height = newHeight;
                y = newY;
              }
              break;
            }
            default:
              break;
          }

          return { ...prev, x, y, width, height };
        }

        return prev;
      });

      // Update cursor
      boxRef.current.style.cursor = "auto";
      parentRef.current.style.cursor = "auto";

      if (box?.isDragging) {
        boxRef.current.style.cursor = "grabbing";
        parentRef.current.style.cursor = "grabbing";
        return;
      }
      const dir = box?.resizeDir || getResizeDir(e, rect);

      if (dir) {
        boxRef.current.style.cursor = cursors[dir];
        parentRef.current.style.cursor = cursors[dir];
      }
    },
    [box, minWidth, minHeight, setBox]
  );

  const handleMouseUp = useCallback(() => {
    setBox(prev => ({
      ...prev,
      isDragging: false,
      isResizing: false,
      resizeDir: null,
    }));
  }, [setBox]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return ReactDOM.createPortal(
    <div
      ref={parentRef}
      className={classes.container}
      style={{
        pointerEvents: box?.isDragging || box?.isResizing ? "auto" : "none",
      }}
    >
      <div
        ref={boxRef}
        className={classes.draggableAndResizableDiv}
        style={{
          left: box?.x || 0,
          top: box?.y || 0,
          width: box?.width || minWidth,
          height: box?.height || minHeight,
        }}
        onMouseDown={handleMouseDownForResizing}
      >
        <div
          ref={dragRef}
          onMouseDown={handleMouseDownForDragging}
          className={classes.dragContainer}
          style={
            !box?.isResizing
              ? { cursor: box?.isDragging ? "grabbing" : "grab" }
              : {}
          }
        >
          <div className={classes.drag} />
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};
export default DraggableAndResizableDiv;
